import orderBy from "lodash/orderBy";
import Layout from "../../components/Layout";
import { styled } from "../../stitches.config";
import { groupBy } from "lodash";

// 1. Consolidated Types
export type ExhibitionType = "solo" | "group" | "project";

export interface CvEntryProps {
  year: number;
  title: string;
  role?: string; // This now does the heavy lifting for description
  venue?: string;
  city: string;
  type: ExhibitionType;
  link?: string;
}

// 2. Updated Data
export const cv: CvEntryProps[] = [
  // 2026
  {
    year: 2026,
    title: "SEAF 2026",
    role: "Invited Artist",
    venue: "Seattle Center",
    city: "Seattle, WA",
    type: "group",
  },
  // 2025
  {
    year: 2024,
    title: "Seatrip",
    role: "Performance",
    city: "Seattle, WA",
    type: "project", // Merged
  },
  {
    year: 2026,
    title: "Merlantis",
    role: "Art Lead",
    venue: "Base Camp 2 Gallery",
    city: "Seattle, WA",
    type: "project",
    link: "https://www.truemisschiff.com/merlantis",
  },
  {
    year: 2025,
    title: "Seacompression Art Gallery",
    role: "Curator",
    city: "Seattle, WA",
    type: "project", // Merged
  },
  {
    year: 2025,
    title: "Male Bodies",
    venue: "Gallery Erato",
    city: "Seattle, WA",
    type: "group",
  },
  {
    year: 2025,
    title: "Counterculture",
    venue: "Gallery Erato",
    city: "Seattle, WA",
    type: "solo",
  },
  {
    year: 2025,
    title: "SEAF 2025",
    venue: "Seattle Center",
    city: "Seattle, WA",
    type: "group",
  },
  // 2024
  {
    year: 2024,
    title: "Critical NW",
    role: "Art Lead, Designer",
    city: "Seattle, WA",
    type: "project",
  },
  {
    year: 2024,
    title: "Colorgasm",
    venue: "Gallery Erato",
    city: "Seattle, WA",
    type: "group",
  },
  {
    year: 2024,
    title: "SEAF 2024",
    venue: "Seattle Center",
    city: "Seattle, WA",
    type: "group",
  },
  // 2023
  {
    year: 2023,
    title: "Traintrip",
    role: "Performance",
    city: "Seattle, WA",
    type: "project", // Merged
  },
  {
    year: 2023,
    title: "SEAF 2023",
    venue: "Seattle Center",
    city: "Seattle, WA",
    type: "group",
  },
  {
    year: 2023,
    title: "Seacompression",
    city: "Seattle, WA",
    type: "group",
  },
  // 2022
  {
    year: 2022,
    title: "Retail Therapy",
    city: "Seattle, WA",
    type: "solo",
  },
  {
    year: 2022,
    title: "SEAF 2022",
    venue: "Seattle Center",
    city: "Seattle, WA",
    type: "group",
  },
  {
    year: 2022,
    title: "Seacompression",
    city: "Seattle, WA",
    type: "group",
  },
  // 2021 & Prior
  {
    year: 2021,
    title: "Cafe Lulu",
    city: "Seattle, WA",
    type: "solo",
  },
  {
    year: 2021,
    title: "Seattle Art Post",
    city: "Kirkland, WA",
    type: "group",
  },
  {
    year: 2019,
    title: "Seacompression",
    city: "Seattle, WA",
    type: "group",
  },
] as CvEntryProps[];
export const cvData = groupBy(orderBy(cv, ["year", "name"], "desc"), "type");

const CvEntry = ({ year, title, role, venue, city, link }: CvEntryProps) => (
  <>
    {/* Year */}
    <span
      style={{
        fontWeight: "bold",
        minWidth: "3.5em",
        display: "inline-block",
      }}
    >
      {year}
    </span>

    {/* Title (Link conditional) */}
    {link ? (
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: "inherit",
          textDecoration: "none",
          borderBottom: "1px dotted #ccc",
        }}
      >
        <span style={{ fontStyle: "italic" }}>{title}</span>
      </a>
    ) : (
      <span style={{ fontStyle: "italic" }}>{title}</span>
    )}

    {/* Role (e.g. " (Art Lead)") */}
    {role && <span> ({role})</span>}

    {/* Venue & City */}
    <span>
      {venue ? `, ${venue}` : ""}, {city}
    </span>
  </>
);

const AboutContainer = styled("div", {
  width: "90%",
  margin: "auto",
});

export const About = () => {
  return (
    <Layout>
      <AboutContainer>
        <h1>About</h1>
        <div>
          <p>
            My name is Miguel Eduardo. I am a Seattle based artist working in
            conceptual, low-brow, pop-surrealist and visionary genres. My work
            is centered around non-ordinary experiences; around the erotic,
            mystical, mythical, and psychedelic. My subject matter is varied,
            but most recently I am known for my erotic work and portraits of
            Seattle socialites in the art and music scenes.
          </p>
          <p>
            I am a versatile artist, as comfortable with oils as with clay,
            metal, plastics, spray paint and digital media. In this age of easy
            reproduction, I focus on creating work in physical media that is
            best appreciated face to face. I want the act of being present in
            front of the work to be an act of exploration and discovery, often
            adding material details that cannot be conveyed through image alone.
            The rendered visual style causally dances between traditional
            representation and heavy graphics.
          </p>
        </div>
        <div>
          <p>
            I also moonlight as a DJ. Check me out at{" "}
            <a href="https://www.migueleduardodj.com/">
              www.migueleduardodj.com
            </a>
            . I specialize in Latin, from house to salsa, but I'm down to play
            just about anything.
          </p>
        </div>
        <div>
          <h3>CV</h3>
          {["solo", "group", "project"].map((group, index) => {
            return (
              <div key={group + "_" + index}>
                <h4>
                  {
                    {
                      solo: "Solo Shows",
                      group: "Group Shows",
                      project: "Selected Projects",
                    }[group]
                  }
                </h4>
                <ul>
                  {cvData[group].map((entry, index) => (
                    <li key={entry.title + "_" + index}>
                      <CvEntry {...entry} />
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div>
          <h3>Education</h3>
          <p>Virginia Commonwealth University, BFA Sculpture, 2005</p>
        </div>
      </AboutContainer>
    </Layout>
  );
};
export default About;
